import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, MapPin, Star, Navigation, Plug,
} from 'lucide-react';
import { PhotoHero, StatusPill } from '@/components/primitives';
import { StallList } from '@/components/station/StallList';
import { FavoriteHeart } from '@/components/station/FavoriteHeart';
import {
  fetchPublicLocation,
  fetchLocationReviewEligibility,
  awardLocationReviewPoints,
  type PublicLocationDetailCharger,
  type LocationReviewEligibility,
} from '@/lib/api';
import { resolveStationHeroUrl } from '@/lib/stationHero';
import { stationGuestMarkupBps, stationGuestNotice } from '@/lib/guestPricing';
import { isPrivateOnlyStation } from '@/lib/privateCharger';
import { PrivateChargerNotice } from '@/components/station/PrivateChargerNotice';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './StationDetailPage.module.css';

/** Real Location _id is a 24-hex ObjectId. Legacy / demo slug IDs used
 *  to be handled here via mockStations.ts; that path has been removed
 *  so only ObjectIds resolve. Slug IDs now fall through to "not found". */
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

interface Props { locationId: string; onNavigate: (path: string) => void }

/**
 * Derived view-model from the real Location + Charger docs.
 *
 * The previous iteration read from apps/web/src/data/mockStations.ts,
 * which never got wired to the API — hitting this page with a real
 * Mongo _id dropped the driver into "Station not found". Now the page
 * calls fetchPublicLocation + fetchPublicLocationDetailChargers via React Query and
 * derives the fields the UI needs (status pill, stall counts, connector
 * rows) from live data.
 *
 * Rich metadata from the old mock (amenities, facilities, hero photos,
 * subtitle copy) lives on the backlog under the Location schema
 * enrichment ticket. Until that lands the hero is a branded gradient
 * tile and the surface focuses on the fields the real model carries.
 *
 * One step (2026-10-23): the overview and the stall list with a Start
 * button per stall render together, the same list the map's location
 * sheet shows. The old "Choose a stall" second screen, with its radio
 * picker and pinned Start, is gone. The map no longer links here; this
 * page serves deep links, QR stickers without a charger, Favorites and
 * the pre-session back button.
 */
export function StationDetailPage({ locationId, onNavigate }: Props) {
  const { t } = useTranslation('station');

  const isValidId = OBJECT_ID_RE.test(locationId);

  // Single round-trip: the driver location-detail endpoint embeds
  // the filtered per-charger list. Eliminates the v1 second hit to
  // GET /chargers (tenant-scoped — would 404 for cross-tenant
  // guests). Embedded list is already filtered to chargers visible
  // to THIS user's perspective, with per-stall access_mode + markup
  // attached.
  const locationQ = useQuery({
    queryKey: ['public-location', locationId],
    queryFn: () => fetchPublicLocation(locationId),
    enabled: isValidId,
  });

  const isLoading = locationQ.isLoading;
  const location = locationQ.data;
  // Use the embedded charger type so `access_mode` + `guest_markup_bps`
  // flow through to the badge logic below. Earlier this was cast to
  // PublicLocationDetailCharger, which dropped the per-stall guest fields.
  const chargers = (location?.chargers ?? []) as PublicLocationDetailCharger[];

  // Derive the fields the UI needs from whatever the API handed us.
  const view = useMemo<StationView | null>(() => {
    if (!location) return null;
    const activeChargers = chargers.filter((c) => c.is_active);
    const onlineChargers = activeChargers.filter((c) => c.is_online);
    const addressLine = [location.address.street, location.address.city].filter(Boolean).join(', ');
    const subtitle = [location.address.city, location.address.state].filter(Boolean).join(' · ')
      || (location.type ? t(`locationType.${location.type}`, { defaultValue: '' }) : '')
      || t('subtitleFallback');
    // Aggregate guest markup across stalls. Picks the max so the
    // header badge doesn't under-promise on mixed-cross-tenant
    // locations; null when no active charger resolves to guest. A stall
    // that turns this driver away for want of a guest price never counts
    // (item 6), and the Api sends no markup for a resident's charger,
    // whose guests are never billed one.
    const guestMarkupBps = stationGuestMarkupBps(activeChargers);
    const guestNotice = stationGuestNotice(activeChargers);
    // Live availability — Block 1 enrichment. A stall is "available
    // right now" when it's both online AND at least one connector
    // reports OCPP status 'Available' (i.e. not Charging / Preparing
    // / Finishing / SuspendedEV* / Reserved / Faulted / Unavailable).
    // Falls back to `is_online` when the embedded connectors array
    // is empty / undefined so the count never under-reports relative
    // to the pre-enrichment behavior.
    const stallsAvailableNow = onlineChargers.filter((c) => {
      if (!Array.isArray(c.connectors) || c.connectors.length === 0) return true;
      return c.connectors.some((conn) => conn.status === 'Available');
    }).length;
    const stallsInUseNow = onlineChargers.length - stallsAvailableNow;

    // Block 3 — connector type/speed summary across all visible stalls.
    // De-duped type list for the chip row; min/max kW for "fastest /
    // slowest" copy. Single-type single-power locations collapse the
    // range to "X kW".
    const allConnectors = activeChargers.flatMap((c) => c.connectors ?? []);
    const connectorTypes = Array.from(
      new Set(allConnectors.map((c) => c.type).filter(Boolean)),
    );
    const powerKws = allConnectors
      .map((c) => c.max_power_kw)
      .filter((n): n is number => typeof n === 'number' && n > 0);
    const minKw = powerKws.length > 0 ? Math.min(...powerKws) : null;
    const maxKw = powerKws.length > 0 ? Math.max(...powerKws) : null;

    // Block 7 — directions deep link. Falls back to a generic Google
    // Maps search-by-address if the coords are missing (older
    // Location docs that predate the coords field).
    const coords = location.address.coordinates;
    const mapsUrl = coords
      ? `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`
      : addressLine
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`
        : null;

    return {
      name: location.name,
      address: addressLine,
      subtitle,
      // Photo chain: operator photo_url → Street View Static at the
      // station's coordinates → null (caller falls back to gradient).
      // Same helper the map popup + bottom sheet use, so a station
      // with blank photo_url renders the same Street View image on
      // every surface that talks about it.
      photoUrl: resolveStationHeroUrl(location),
      buildingType: location.type,
      stallsTotal: activeChargers.length,
      stallsAvailable: onlineChargers.length,
      stallsAvailableNow,
      stallsInUseNow,
      openStatus: onlineChargers.length > 0 ? 'open_now' as const : 'closed' as const,
      activeChargers,
      guestMarkupBps,
      guestNotice,
      connectorTypes,
      minKw,
      maxKw,
      mapsUrl,
    };
  }, [location, chargers, t]);

  // Handle invalid ID or not-found upfront.
  if (!isValidId) {
    return <NotFound onBack={() => onNavigate('/map')} />;
  }
  if (isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.notFound}>
          <Loader2 size={16} className={styles.spin} /> {t('loading')}
        </p>
      </div>
    );
  }
  if (locationQ.isError || !location || !view) {
    return <NotFound onBack={() => onNavigate('/map')} />;
  }
  // The private switch (Warren, 2026-10-22): every stall this driver could
  // see here is a private charger they may not start. The page names the
  // site and says "This is a private charger" where the stalls and their
  // Start buttons would be.
  if (isPrivateOnlyStation(location)) {
    return (
      <div className={styles.page}>
        <div className={styles.topRow}>
          <button className={styles.backFab} aria-label={t('back')} onClick={() => onNavigate('/map')}>
            <ArrowLeft size={16} />
          </button>
          <div className={styles.titleBlock}>
            <p className={styles.eyebrow}>{view.subtitle}</p>
            <h1 className={styles.title}>{view.name}</h1>
            {view.address && <p className={styles.subtitle}>{view.address}</p>}
          </div>
        </div>
        <PrivateChargerNotice
          ns="station"
          className={styles.notFoundCard}
          titleClassName={styles.notFoundTitle}
          hintClassName={styles.notFoundHint}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.topRow}>
        <button
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/map')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>{view.subtitle}</p>
          <h1 className={styles.title}>{view.name}</h1>
          <p className={styles.subtitle}>{view.address || '—'}</p>
          {/* Guest pricing badge (commit 12 of third-party-charger-
              access). Renders only when at least one stall here
              resolves to access_mode='guest' for THIS user — the
              embedded chargers array carries that flag per row.
              Tenant-only views see nothing. */}
          {view.guestMarkupBps != null && view.guestMarkupBps > 0 && (
            <span className={styles.guestPricingBadge}>
              {t('guestPricing', { pct: formatMarkupPct(view.guestMarkupBps) })}
            </span>
          )}
          {/* Item 6: every stall here is a resident's charger open to
              guests with no guest price, so this driver cannot start. Said
              up front, before they pick a stall and reach a locked Start. */}
          {view.guestNotice.everyStall && (
            <p className={styles.guestNotOpenNote} role="note">
              {t(view.guestNotice.count > 1 ? 'guestNotOpenYetAll' : 'guestNotOpenYet')}
            </p>
          )}
        </div>
      </div>

      <div className={styles.fade}>
        <StationOverview view={view} stationId={location._id} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

// ── Derived-shape for local use ──────────────────────────────────────────────
interface StationView {
  name: string;
  address: string;
  subtitle: string;
  /** Resolved hero photo URL — operator photo_url, then Street View
   *  Static at the station's lat/lng. Null when neither tier
   *  produces a URL; caller renders the gradient placeholder. See
   *  `lib/stationHero.ts` for the full resolution chain (shared
   *  with the map popup + bottom sheet). */
  photoUrl: string | null;
  /** Location building type (lowercase enum: residential / multifamily /
   *  commercial / public / fleet). Surfaced as a labeled value at the start
   *  of the connectors row. */
  buildingType: string;
  stallsTotal: number;
  stallsAvailable: number;
  /** Block 1 enrichment — true "available right now" count: online
   *  stalls whose at least one connector reports OCPP 'Available'.
   *  May be lower than `stallsAvailable` when stalls are mid-session
   *  (Charging / Preparing / Finishing / Suspended*). */
  stallsAvailableNow: number;
  /** Online stalls currently mid-session (i.e. `stallsAvailable -
   *  stallsAvailableNow`). Shown in the live-availability copy
   *  when > 0. */
  stallsInUseNow: number;
  openStatus: 'open_now' | 'closed';
  activeChargers: PublicLocationDetailCharger[];
  /** Guest pricing markup in basis points when at least one stall
   *  resolves to access_mode='guest' for this user. Null on
   *  tenant-only locations. */
  guestMarkupBps: number | null;
  /** Item 6: stalls that would turn this driver away as a paying guest
   *  (a resident's charger with no guest price). everyStall drives the
   *  header note; otherwise each flagged stall says it. */
  guestNotice: { anyStall: boolean; everyStall: boolean; count: number };
  /** Block 3 — de-duped connector type list across all visible
   *  stalls (e.g. ['CCS1', 'Type2']). Empty when no charger data. */
  connectorTypes: string[];
  /** Block 3 — slowest / fastest stall power in kW. Null when no
   *  charger has populated max_power_kw. Equal when all stalls
   *  share one power tier. */
  minKw: number | null;
  maxKw: number | null;
  /** Block 7 — Google Maps directions deep link from the location's
   *  coordinates (or address-fallback search URL). Null only when
   *  both coords AND address are absent. */
  mapsUrl: string | null;
}

// Location-type eyebrow labels ("X station") and the short building-type
// labels for the connectors-row value are resolved at call sites via
// t('locationType.<type>') / t('buildingType.<type>') so both track the
// driver's locale. Mirrors the admin Add Location TYPE options.

// Branded gradient tile used as the final fallback when neither the
// operator photo_url NOR the Street View Static URL resolves (no
// coordinates, or the maps key is missing). Same data URI the
// PreSessionPage uses so the two screens read as one product.
const HERO_PLACEHOLDER =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='320' viewBox='0 0 800 320'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%2321c163' stop-opacity='0.4'/%3E%3Cstop offset='100%25' stop-color='%230a0a0a' stop-opacity='0.9'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='320' fill='url(%23g)'/%3E%3C/svg%3E";

/** Render guest_markup_bps (basis points) as a percent string for
 *  display. Mirrors the PreSessionPage formatter — 1 dp keeps
 *  "+20%" clean and "+8.5%" precise without trailing zeros. */
function formatMarkupPct(bps: number): string {
  const pct = bps / 100;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
}

function openStatusPill(status: StationView['openStatus'], t: (key: string) => string) {
  if (status === 'open_now') {
    return <StatusPill tone="good" variant="solid" size="sm">{t('pill.available')}</StatusPill>;
  }
  return <StatusPill tone="warn" variant="solid" size="sm">{t('pill.offline')}</StatusPill>;
}

// ── Overview + stalls ────────────────────────────────────────────────────────
function StationOverview({
  view, stationId, onNavigate,
}: { view: StationView; stationId: string; onNavigate: (path: string) => void }) {
  const { t } = useTranslation('station');
  // Eligibility probe for the location-scoped review CTA. Mount-time
  // fire so the CTA renders in its correct state (enabled / cooldown /
  // hidden) from first paint. Cached at 60s — the cooldown window is
  // 60 days, so over-fetching here has zero correctness implication
  // (the result barely changes within a session). The query suspends
  // gracefully via `isLoading`; we render nothing during the suspense
  // so the page doesn't flash an enabled CTA that then snaps to
  // cooldown.
  const eligibilityQ = useQuery({
    queryKey: ['location-review-eligibility', stationId],
    queryFn: () => fetchLocationReviewEligibility(stationId),
    staleTime: 60_000,
  });

  return (
    <>
      <PhotoHero
        src={view.photoUrl || HERO_PLACEHOLDER}
        alt={view.name}
        // 160 (was 200) so the stall list starts higher now that it shares
        // the screen with the overview.
        height={160}
        topLeft={openStatusPill(view.openStatus, t)}
        topRight={<FavoriteHeart stationId={stationId} className={styles.heartBtn} idleColor="#FFFFFF" />}
        bottomLeft={
          // Photo-overlay stall line surfaces live availability —
          // "X / Y stalls online" stays as the headline ratio, but
          // the *meaningful* number drivers care about is the
          // available-right-now subset. When all online stalls are
          // mid-session we surface that explicitly so the driver
          // doesn't assume "online == open."
          <div className={styles.stallsLine}>
            <strong>{view.stallsAvailable}/{view.stallsTotal}</strong>
            <span className={styles.stallsLabel}>{t('stallsOnline')}</span>
          </div>
        }
      />

      {/* Block 1: live availability + connector summary + maps
          link. Each card renders only when its data resolves; a
          location with no chargers / no coords degrades gracefully
          to the stall list's empty line below. */}
      <StationQuickInfo view={view} />

      {/* The stalls, each with its own Start. The header note already
          covers "every stall isn't open to guests yet", so the per-stall
          note is off in that case. */}
      <section aria-labelledby="station-stalls-title">
        <div className={styles.sectionHeader}>
          <h2 id="station-stalls-title" className={styles.sectionLabel}>{t('stall.title')}</h2>
        </div>
        <StallList
          locationId={stationId}
          chargers={view.activeChargers}
          onNavigate={onNavigate}
          emptyText={t('stall.empty')}
          showGuestNotes={!view.guestNotice.everyStall}
        />
      </section>

      {/* Block 2 / 4 / 5 / 6 (pricing snapshot, amenities, operating
          hours, recent-activity counts) are deferred to a follow-up
          ticket — the Location schema doesn't carry those fields
          today, and the public endpoint doesn't expose tariff
          resolution or sessions-by-location. See
          docs/tickets/location-driver-enrichment.md for the full
          gap list. */}

      {/* Location-scoped review CTA. Hidden when the eligibility
          probe reports `reason: 'not_reviewable'` (location has no
          google_place_id). Renders disabled with next-eligible-at
          copy on cooldown. Visual continuity with the session-
          scoped CTA on SessionCompletePage. */}
      {eligibilityQ.data && eligibilityQ.data.reason !== 'not_reviewable' && (
        <LocationReviewCta
          locationId={stationId}
          initial={eligibilityQ.data}
        />
      )}
    </>
  );
}

/**
 * Leave-a-Google-review CTA for the station detail page. Mirrors
 * the session-scoped ReviewCta on SessionCompletePage in both
 * visual treatment and order-of-operations:
 *
 *   1. window.open synchronously inside the click handler so iOS
 *      Safari's popup heuristic accepts it as user-initiated.
 *   2. Fire the points POST in parallel with the user's review
 *      action — the review-write surface is useful regardless of
 *      whether the points credit lands.
 *   3. State updates from the POST response.
 *
 * Five internal states (vs the session CTA's three) — the extra
 * two cover the cooldown semantic that's specific to the
 * location-scoped award:
 *
 *   - available  : enabled, "Leave a Google review (+5 pts)"
 *   - pending    : disabled, "Awarding…" during in-flight POST
 *   - awarded    : disabled, "+5 awarded"
 *   - cooldown   : disabled, "Already reviewed · eligible <date>"
 *   - error      : enabled (retry), "Tap to retry (+5 pts)"
 *
 * Initial state derived from the parent's eligibility probe so the
 * first paint is correct without a fetch-then-flash cycle.
 */
/**
 * Quick-info card block — surfaces three densities the driver
 * actually needs to commit to a station:
 *
 *   1. Live availability ("3 available · 2 in use" beats "5/5
 *      stalls online" — drivers care about *now*, not the static
 *      capacity).
 *   2. Connector + speed summary (Type / kW range chips so the
 *      driver knows their car is compatible before tapping into
 *      the per-stall picker).
 *   3. Get directions link — opens a native maps app via the
 *      Google Maps URL scheme (iOS Safari + Android Chrome both
 *      route this through their respective maps app when
 *      installed; falls back to the in-browser map otherwise).
 *
 * Each row renders only when its data exists. A location with no
 * coords + no chargers (degenerate seed data) collapses the
 * entire block to null, keeping the page clean.
 */
function StationQuickInfo({ view }: { view: StationView }) {
  const { t } = useTranslation('station');
  // No data → no block. Prevents an empty card-shaped gap below
  // the photo when the location predates the enrichment fields.
  const hasAvailability = view.stallsTotal > 0;
  const hasConnectors = view.connectorTypes.length > 0 || view.maxKw !== null;
  const hasDirections = view.mapsUrl !== null;
  if (!hasAvailability && !hasConnectors && !hasDirections) return null;

  // Per-state breakdown across every connector at this location, split
  // into the five driver-facing buckets. A connector on an OFFLINE charger
  // counts as Unavailable regardless of its (now stale) last-known status —
  // the same is_online-wins rule the map uses. 'Occupied' is bucketed as
  // Charging (in-use: the transaction is bound to the connector, even if
  // not yet power-flowing). Any unknown/future status (e.g. 'Removed', not
  // in today's enum) falls into Unavailable so it never silently vanishes
  // from the totals.
  const allConnectors = view.activeChargers.flatMap((c) => c.connectors ?? []);
  const total = allConnectors.length;
  const counts = {
    available: 0,
    preparing: 0,
    charging: 0,
    pluggedIn: 0,
    unavailable: 0,
  };
  for (const c of view.activeChargers) {
    for (const conn of c.connectors ?? []) {
      if (!c.is_online) { counts.unavailable += 1; continue; }
      switch (conn.status) {
        case 'Available': counts.available += 1; break;
        case 'Preparing': counts.preparing += 1; break;
        case 'Charging':
        case 'SuspendedEVSE':
        case 'Occupied': counts.charging += 1; break;
        case 'SuspendedEV':
        case 'Finishing': counts.pluggedIn += 1; break;
        case 'Unavailable':
        case 'Faulted':
        case 'Reserved': counts.unavailable += 1; break;
        default: counts.unavailable += 1; break;
      }
    }
  }

  return (
    <section className={styles.quickInfo} aria-label={t('quickInfo.sectionAria')}>
      {hasAvailability && allConnectors.length > 0 && (
        <div className={styles.statusGrid} role="group" aria-label={t('quickInfo.stallStatusAria')}>
          {/* Hero — Available is THE number a driver acts on: a wide
              full-width tile with the oversized count centered as the
              focal point. Count, dot, and color wiring unchanged. */}
          <StatusBox count={counts.available} total={total} variant="available" label={t('status.available')} size="hero" />
          {/* Quadrant — the four secondary states, 2×2 under the hero
              (no empty cell). Same counts, labels, and status colors. */}
          <div className={styles.statusGridQuad}>
            <StatusBox count={counts.charging}    total={total} variant="charging"    label={t('status.charging')}    />
            <StatusBox count={counts.preparing}   total={total} variant="preparing"   label={t('status.preparing')}   />
            <StatusBox count={counts.pluggedIn}   total={total} variant="pluggedIn"   label={t('status.pluggedIn')}  />
            <StatusBox count={counts.unavailable} total={total} variant="unavailable" label={t('status.unavailable')} />
          </div>
        </div>
      )}

      {hasConnectors && (
        <div className={`${styles.quickRow} ${styles.quickRowWithType}`}>
          <span className={styles.quickIcon}><Plug size={16} /></span>
          <div className={styles.quickBody}>
            <p className={styles.quickLabel}>{t('quickInfo.buildingType')}</p>
            <p className={styles.quickValue}>
              {t(`buildingType.${view.buildingType}`, { defaultValue: '' }) || view.buildingType || '—'}
            </p>
          </div>
          <div className={styles.quickBody}>
            <p className={styles.quickLabel}>{t('quickInfo.connectors')}</p>
            <div className={styles.quickValue}>
              <div className={styles.connectorChips}>
                {view.connectorTypes.map((t) => (
                  <span key={t} className={styles.connectorChip}>{t}</span>
                ))}
              </div>
              {view.maxKw !== null && view.minKw !== null && (
                <span className={styles.quickSub}>
                  {view.minKw === view.maxKw
                    ? `${view.maxKw} kW`
                    : `${view.minKw}–${view.maxKw} kW`}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {hasDirections && view.mapsUrl && (
        <a
          className={`${styles.quickRow} ${styles.quickRowLink}`}
          href={view.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className={styles.quickIcon}><Navigation size={16} /></span>
          <div className={styles.quickBody}>
            <p className={styles.quickLabel}>{t('quickInfo.getDirections')}</p>
            <p className={styles.quickValue}>
              <strong>{t('quickInfo.openInMaps')}</strong>
            </p>
          </div>
        </a>
      )}
    </section>
  );
}

/**
 * One cell of the 3-up status grid. Always renders (even at
 * count 0) so the layout doesn't shift when a stall changes
 * state; cells with count 0 dim to 50% opacity per the spec.
 *
 * The dot's `::before` glow halo gets its color from the
 * variant-specific class in CSS — keeping the color tokens in
 * the stylesheet rather than inlined as `style={{ background }}`
 * so `prefers-reduced-motion` can also disable the pulse on the
 * `.charging` variant only.
 */
function StatusBox({
  count, total, variant, label, size = 'sm',
}: {
  count: number;
  total: number;
  variant: 'available' | 'preparing' | 'charging' | 'pluggedIn' | 'unavailable';
  label: string;
  size?: 'sm' | 'hero';
}) {
  const { t } = useTranslation('station');
  const dimmed = count === 0;
  // Every tile is icon-free — the colored dot inline with the label
  // carries each status cue (the old tinted 40px icon row went first
  // as iconographic noise; the hero's bolt Lottie followed it per
  // founder review — the centered count reads cleaner alone). The
  // HERO tile is the same centered cluster at display scale, its
  // count a fraction of the location's total connectors.
  const body = (
    <>
      <span className={styles.statusValue}>{count}<span className={styles.statusValueTotal}>/{total}</span></span>
      <span className={styles.statusLabelRow}>
        <span
          className={`${styles.statusDot} ${styles[`statusDot_${variant}`] ?? ''}`}
          aria-hidden
        />
        <span className={styles.statusLabel}>{label}</span>
      </span>
    </>
  );
  return (
    <div
      className={`${styles.statusBox} ${size === 'hero' ? styles.statusBoxHero : ''} ${dimmed ? styles.statusBoxDim : ''}`}
      aria-label={t('status.boxAria', { label, n: count, total })}
    >
      {body}
    </div>
  );
}

type LocationCtaState =
  | { kind: 'available' }
  | { kind: 'pending' }
  | { kind: 'awarded'; pointsAwarded: number }
  | { kind: 'cooldown'; nextEligibleAt: string }
  | { kind: 'error'; message: string };

function LocationReviewCta({
  locationId, initial,
}: {
  locationId: string;
  /** Eligibility-probe result with reason in {'eligible',
   *  'cooldown_active'} — the parent narrows out 'not_reviewable'
   *  before rendering this component. */
  initial: Extract<
    LocationReviewEligibility,
    { reason: 'eligible' | 'cooldown_active' }
  >;
}) {
  const { t } = useTranslation('station');
  const [state, setState] = useState<LocationCtaState>(() =>
    initial.reason === 'cooldown_active'
      ? { kind: 'cooldown', nextEligibleAt: initial.next_eligible_at }
      : { kind: 'available' },
  );

  // The Google Maps review-write surface. encodeURIComponent is
  // defensive — place ids don't currently contain any characters
  // that need escaping, but Google's variants are open-shape and
  // future formats may.
  const reviewUrl = `https://search.google.com/local/writereview?placeid=${encodeURIComponent(initial.google_place_id)}`;

  const handleClick = (): void => {
    if (state.kind === 'awarded' || state.kind === 'pending' || state.kind === 'cooldown') return;

    // Open synchronously in the user-gesture handler tick. iOS
    // Safari blocks window.open after an async response. The tracked
    // redirect (when the eligibility payload minted one) is
    // preferred — the server logs the click, awards on the same
    // 60-day window key, and forwards to Google; old servers omit
    // the field and the local URL behaves exactly as before. Both
    // URLs are already in hand, so the open stays synchronous.
    window.open(initial.review_redirect_url ?? reviewUrl, '_blank', 'noopener,noreferrer');

    setState({ kind: 'pending' });
    // Belt-and-suspenders for one release: the redirect endpoint
    // already awards on the same
    // location_review:<locationId>:<userId>:<windowId> key, so this
    // POST covers a blocked popup or an expired token and can never
    // double-pay — whichever path lands second collapses to an
    // idempotent no-op. A later cleanup removes it once every client
    // routes through the redirect.
    void awardLocationReviewPoints(locationId)
      .then((result) => {
        if (result.already_awarded && result.next_eligible_at) {
          // Race case: the eligibility probe said we were eligible
          // but the server saw a competing write. Flip to cooldown
          // with the server's reported next-eligible-at.
          setState({ kind: 'cooldown', nextEligibleAt: result.next_eligible_at });
        } else {
          setState({ kind: 'awarded', pointsAwarded: result.points });
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to award points';
        setState({ kind: 'error', message: msg });
      });
  };

  const disabled =
    state.kind === 'awarded'
    || state.kind === 'pending'
    || state.kind === 'cooldown';

  const label = (() => {
    switch (state.kind) {
      case 'awarded':
        return t('review.awarded', { points: state.pointsAwarded });
      case 'pending':
        return t('review.pending');
      case 'cooldown':
        return t('review.cooldown', { date: formatEligibleAgain(state.nextEligibleAt) });
      case 'error':
        return t('review.retry');
      case 'available':
      default:
        return t('review.available');
    }
  })();

  const ariaLabel = (() => {
    switch (state.kind) {
      case 'awarded':
        return t('review.ariaAwarded', { points: state.pointsAwarded });
      case 'cooldown':
        return t('review.ariaCooldown', { date: formatEligibleAgain(state.nextEligibleAt) });
      default:
        return t('review.ariaDefault');
    }
  })();

  return (
    <button
      type="button"
      className={styles.reviewCta}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-state={state.kind}
    >
      <NamedIcon
        name="star"
        fallback={<Star size={16} className={styles.reviewIcon} aria-hidden />}
        size={16}
      />
      <span className={styles.reviewLabel}>{label}</span>
    </button>
  );
}

/** Short date label for the cooldown copy. Renders as a calendar
 *  date (e.g. "Jan 12") rather than relative ("in 47 days") since
 *  60-day cooldowns make relative copy feel imprecise. Falls back
 *  to the raw ISO string on Intl-format failure (e.g. malformed
 *  server input) so the user still gets something rather than
 *  empty space. */
function formatEligibleAgain(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// ── Not-found empty state ────────────────────────────────────────────────────
function NotFound({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('station');
  return (
    <div className={styles.page}>
      <button className={styles.backInline} onClick={onBack}>
        <ArrowLeft size={16} /> {t('notFound.backToMap')}
      </button>
      <div className={styles.notFoundCard}>
        <NamedIcon
          name="mappin"
          fallback={<MapPin size={22} className={styles.notFoundIcon} />}
          size={22}
        />
        <p className={styles.notFoundTitle}>{t('notFound.title')}</p>
        <p className={styles.notFoundHint}>
          {t('notFound.hint')}
        </p>
      </div>
    </div>
  );
}
